import React from 'react';
import {Controller} from 'react-hook-form';
import {
    FormError,
    FormLabel,
    getInputClassNames,
    getInputWrapperClass,
    getWholeDivWrapperClass,
    SUFFIX_CLASS
} from './sharedStyles.tsx';
import {DollarSign} from 'lucide-react';
import {CurrencyComponent, FormComponentProps, ParentController} from "../DynamicFormTypes.ts";
import {useFormMethods} from "../DyanmicFormContext.tsx";
import {useFieldBlurHandler} from "../hooks/OnFieldBlurHanlder.tsx";
import {useValidationRules} from "../hooks/useValidationRules.tsx";

export const CurrencyRenderer: React.FC<FormComponentProps<CurrencyComponent> & {parentController?: ParentController}> = ({ component, parentController = undefined}) => {
    const { formMethods } = useFormMethods()

    // Format with commas
    const formatWithCommas = (value: string | number | undefined): string => {
        if (value === undefined || value === '' || value === null) return '';

        // Remove any non-digit characters
        const numericString = value.toString().replace(/(?!^-)[^\d.]/g, '');
        // Add commas for thousands
        return numericString.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const { handleFieldBlur } = useFieldBlurHandler({
        component
    });

    const renderInput = ({ field, fieldState }: ParentController) => {
        const { onChange, onBlur, value, ref } = field;
        // Get base input classes and add necessary padding for the dollar sign using reactive fieldState
        const inputClasses = component.compactUI
            ? `${getInputClassNames(component, fieldState)} pl-5`
            : `${getInputClassNames(component, fieldState)} pl-10`;

        return (
            <div className={getWholeDivWrapperClass(component)}>
                <FormLabel component={component}/>
                <div className={getInputWrapperClass(component) + " flex"}>
                    {component.compactUI ?
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-1">
                            <DollarSign className="h-4 w-4 text-gray-400"/>
                        </div>
                        :
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <DollarSign className="h-5 w-5 text-gray-400"/>
                        </div>
                    }
                    <input
                        id={component.fieldKey}
                        ref={ref}
                        type="text"
                        inputMode="numeric"
                        placeholder="0"
                        className={inputClasses + ' flex-grow'}
                        value={formatWithCommas(value)}
                        onChange={(e) => {
                            // Remove non-digit characters before saving to form state
                            const numericValue = e.target.value.replace(/(?!^-)-|[^\d.-]/g, '');
                            onChange(numericValue);
                        }}
                        onBlur={(e) => {
                            if (e.target.value === "-") {
                                onChange(e.target.placeholder);
                            }
                            onBlur();
                            handleFieldBlur();
                        }}
                    />
                    {component.suffix &&
                        <span className={SUFFIX_CLASS.replace("text-sm", "text-xs")}>{component.suffix}</span>
                    }
                </div>
                <FormError fieldState={fieldState} component={component} />
            </div>
        );
    }

    if (parentController) {
        //this is used basically so we can register the value ONCE in the derived component
        return renderInput(parentController);
    }

    const validate = useValidationRules({ component });
    return (
        <Controller
            name={component.fieldKey}
            control={formMethods.control}
            rules={validate}
            render={({ field, fieldState }) => renderInput({ field, fieldState })}
        />
    );
};

export const Currency = React.memo(CurrencyRenderer);